import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { carBrands } from "../data/carBrands";
import { findModelImage, getHighResImage, DEFAULT_CAR_IMAGE, isSimilarModelImage } from "../utils/imageMatching";
import { getBrandLogo } from "../utils/brandLogos";

type Candidate = {
  brandId: string;
  brandName: string;
  model: (typeof carBrands)[number]["models"][number];
  price: number;
  image: string;
};

type Attempt = {
  value: number;
  diff: number; // percentatge de desviació respecte al preu real
  direction: "up" | "down" | "hit";
};

const MAX_ATTEMPTS = 5;
// Marge d'encert: si t'hi acostes menys d'un 3%, es dóna per bo
const TOLERANCE = 0.03;

const parsePrice = (price: string): number => {
  const digits = (price || "").replace(/[^\d]/g, "");
  return digits ? parseInt(digits, 10) : 0;
};

// Només models amb preu creïble i amb foto real (no el placeholder)
const CANDIDATES: Candidate[] = carBrands.flatMap(brand =>
  brand.models.flatMap(model => {
    const price = parsePrice(model.price);
    if (price < 8000 || price > 500000) return [];
    const image = findModelImage(brand.id, model.id);
    if (image === DEFAULT_CAR_IMAGE) return [];
    return [{ brandId: brand.id, brandName: brand.name, model, price, image }];
  })
);

const pickCandidate = (exclude?: Candidate): Candidate => {
  let next = CANDIDATES[Math.floor(Math.random() * CANDIDATES.length)]!;
  if (exclude && CANDIDATES.length > 1) {
    while (next.model.id === exclude.model.id && next.brandId === exclude.brandId) {
      next = CANDIDATES[Math.floor(Math.random() * CANDIDATES.length)]!;
    }
  }
  return next;
};

const formatPrice = (value: number): string =>
  `${value.toLocaleString("ca-ES")}€`;

// Pista de proximitat: com més a prop, més "calent"
const proximityLabel = (diff: number): { label: string; color: string } => {
  if (diff <= 0.05) return { label: "Molt a prop", color: "#ef4444" };
  if (diff <= 0.15) return { label: "A prop", color: "#f97316" };
  if (diff <= 0.35) return { label: "Fred", color: "#3b82f6" };
  return { label: "Molt lluny", color: "#64748b" };
};

export const TestPreu = () => {
  const [car, setCar] = useState<Candidate>(() => pickCandidate());
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [guess, setGuess] = useState("");
  const [status, setStatus] = useState<"playing" | "won" | "lost">("playing");
  const [rounds, setRounds] = useState(0);
  const [wins, setWins] = useState(0);
  const [logoError, setLogoError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const remaining = MAX_ATTEMPTS - attempts.length;
  const image = useMemo(() => getHighResImage(car.image, 700), [car]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (status !== "playing") return;

    const value = parseInt(guess.replace(/[^\d]/g, ""), 10);
    if (!value) return;

    const diff = Math.abs(value - car.price) / car.price;
    const attempt: Attempt = {
      value,
      diff,
      direction: diff <= TOLERANCE ? "hit" : value > car.price ? "down" : "up",
    };
    const nextAttempts = [...attempts, attempt];

    setAttempts(nextAttempts);
    setGuess("");

    if (attempt.direction === "hit") {
      setStatus("won");
      setRounds(r => r + 1);
      setWins(w => w + 1);
    } else if (nextAttempts.length >= MAX_ATTEMPTS) {
      setStatus("lost");
      setRounds(r => r + 1);
    }
  };

  const handleNextCar = () => {
    setCar(pickCandidate(car));
    setAttempts([]);
    setGuess("");
    setStatus("playing");
    setLogoError(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const specs = [
    car.model.fuel && { label: "Combustible", value: car.model.fuel },
    car.model.power && { label: "Potència", value: `${car.model.power} CV` },
    car.model.trunk && { label: "Maleter", value: `${car.model.trunk} L` },
    (car.model.length || car.model.width || car.model.height) && {
      label: "Dimensions",
      value: [car.model.length, car.model.width, car.model.height]
        .filter(Boolean)
        .join(" × ") + " mm",
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-2xl">
        {/* Back link */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Inici
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1.5">
              Endevina el preu
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base">
              {MAX_ATTEMPTS} intents. Et diem si el preu real és més alt o més baix.
            </p>
          </div>
          {rounds > 0 && (
            <div className="text-right shrink-0">
              <div className="text-xl font-bold text-foreground leading-none">
                {wins}<span className="text-sm text-muted-foreground">/{rounds}</span>
              </div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-widest mt-1">
                Encerts
              </div>
            </div>
          )}
        </div>

        {/* Car card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="relative w-full h-52 sm:h-64 bg-linear-to-br from-secondary to-muted flex items-center justify-center p-5">
            {isSimilarModelImage(car.brandId, car.model.id) && (
              <span className="absolute left-4 top-4 z-10 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
                Foto d'un model similar
              </span>
            )}
            <img
              src={image}
              alt={`${car.brandName} ${car.model.name}`}
              className="w-full h-full object-contain"
            />
          </div>

          <div className="px-5 sm:px-6 py-5 border-b border-border">
            <div className="flex items-center gap-3">
              {!logoError && (
                <div className="w-10 h-10 rounded-xl bg-white border border-black/10 dark:border-white/15 flex items-center justify-center shrink-0 p-1">
                  <img
                    src={getBrandLogo(car.brandId)}
                    alt={car.brandName}
                    className="w-full h-full object-contain"
                    onError={() => setLogoError(true)}
                  />
                </div>
              )}
              <div className="min-w-0">
                <div className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">
                  {car.brandName}
                </div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight truncate">
                  {car.model.name}
                </h2>
              </div>
            </div>

            {/* Specs */}
            <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3">
              {specs.map(spec => (
                <div key={spec.label}>
                  <dt className="text-[11px] text-muted-foreground uppercase tracking-widest mb-0.5">
                    {spec.label}
                  </dt>
                  <dd className="text-sm font-semibold text-foreground">{spec.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="p-5 sm:p-6">
            {status === "playing" ? (
              <>
                <form onSubmit={handleSubmit} className="flex gap-2.5">
                  <div className="relative flex-1">
                    <input
                      ref={inputRef}
                      type="number"
                      inputMode="numeric"
                      value={guess}
                      onChange={e => setGuess(e.target.value)}
                      placeholder="El teu preu"
                      aria-label="El teu preu en euros"
                      autoFocus
                      className="w-full pl-4 pr-9 py-3 border border-border rounded-xl bg-background text-foreground text-base font-semibold focus:outline-none focus:border-ring transition-colors"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                      €
                    </span>
                  </div>
                  <button
                    type="submit"
                    disabled={!guess}
                    className="px-6 py-3 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity disabled:opacity-30"
                  >
                    Provar
                  </button>
                </form>
                <p className="mt-2.5 text-xs text-muted-foreground">
                  {remaining} {remaining === 1 ? "intent restant" : "intents restants"} · encertes si t'hi acostes menys d'un {Math.round(TOLERANCE * 100)}%
                </p>
              </>
            ) : (
              <div
                className={`rounded-xl border p-5 text-center ${
                  status === "won"
                    ? "border-green-500 bg-green-500/10"
                    : "border-red-500 bg-red-500/10"
                }`}
              >
                <div className="text-3xl mb-2">{status === "won" ? "🎯" : "💸"}</div>
                <p className="text-sm font-semibold text-foreground mb-1">
                  {status === "won"
                    ? `Encertat en ${attempts.length} ${attempts.length === 1 ? "intent" : "intents"}!`
                    : "T'has quedat sense intents"}
                </p>
                <p className="text-2xl font-bold text-foreground">{formatPrice(car.price)}</p>
                <button
                  onClick={handleNextCar}
                  className="mt-5 w-full py-3 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity"
                >
                  Un altre cotxe →
                </button>
              </div>
            )}

            {/* Attempts */}
            {attempts.length > 0 && (
              <ul className="mt-4 space-y-2">
                {attempts.map((attempt, i) => {
                  const proximity = proximityLabel(attempt.diff);
                  return (
                    <li
                      key={i}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-border bg-secondary/50"
                    >
                      <span className="text-xs text-muted-foreground w-4 shrink-0">{i + 1}</span>
                      <span className="text-sm font-semibold text-foreground">
                        {formatPrice(attempt.value)}
                      </span>
                      <span className="ml-auto flex items-center gap-2 shrink-0">
                        {attempt.direction !== "hit" && (
                          <span
                            className="text-[11px] font-semibold uppercase tracking-widest"
                            style={{ color: proximity.color }}
                          >
                            {proximity.label}
                          </span>
                        )}
                        {attempt.direction === "hit" ? (
                          <span className="text-sm font-bold text-green-600 dark:text-green-400">
                            Encertat
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sm font-bold text-foreground">
                            {attempt.direction === "up" ? "Més" : "Menys"}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              {attempt.direction === "up" ? (
                                <>
                                  <path d="M12 19V5" />
                                  <path d="m5 12 7-7 7 7" />
                                </>
                              ) : (
                                <>
                                  <path d="M12 5v14" />
                                  <path d="m19 12-7 7-7-7" />
                                </>
                              )}
                            </svg>
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <p className="mt-6 text-xs text-muted-foreground/60 text-center">
          Preus de partida (versió d'accés) segons el catàleg de Cotx.es
        </p>
      </div>
    </div>
  );
};
